import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { DefaultHeader, MainLayout } from '../../component'

import ProgressIndicator from '@veneer/core/dist/esm/scripts/progress_indicator'
import Button from '@veneer/core/dist/esm/scripts/button'

import udlEvents from '../../common/udlEvents'
import config from '../../config'

import * as S from './styles'

const Loading = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [message, setMessage] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [stage, setStage] = useState(1)
  const [showButtons, setShowButtons] = useState(false)
  const allowBack = typeof window !== 'undefined' ? window.history.length > 1 : false

  useEffect(() => {
    const timers: Array<ReturnType<typeof setTimeout>> = []
    timers.push(
      setTimeout(() => {
        dispatch.udl.trackEvent(udlEvents.getEventByID('EVENT213'))
        setMessage(t('label.wait_msg'))
        setStage(1)
      }, config.load_initial_timer)
    )
    timers.push(
      setTimeout(() => {
        dispatch.udl.trackEvent(udlEvents.getEventByID('EVENT214'))
        setMessage(t('label.loading_taking_long'))
        setStage(2)
      }, config.load_medium_timer)
    )
    timers.push(
      setTimeout(() => {
        if (allowBack) {
          dispatch.udl.trackEvent(udlEvents.getEventByID('EVENT215'))
          setMessage(t('label.loading_error_retry'))
          setStage(3)
        } else {
          dispatch.udl.trackEvent(udlEvents.getEventByID('EVENT216'))
          setMessage(t('label.loading_error_retry'))
          setStage(3)
        }
        setHint(t('label.loading_error_retry_hint'))
        setShowButtons(true)
      }, config.load_long_timer)
    )
    return () => {
      timers.forEach(clearTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch.udl, allowBack])

  const retryButton = () => {
    setLoading(true)
    dispatch.user.update({ flow: '', flowId: '' })

    // Decide what event to be called based on allowBack
    const eventId = allowBack ? 'EVENT217' : 'EVENT219'
    dispatch.udl.trackEvent(udlEvents.getEventByID(eventId))

    window.location.reload()
  }

  const backButton = () => {
    setLoading(true)
    dispatch.udl.trackEvent(udlEvents.getEventByID('EVENT218'))
    dispatch.user.handleBackLink()
  }

  const renderButtons = () => (
    <S.LoadingButtons>
      {allowBack && (
        <S.ButtonWrapper>
          <Button
            id="back-link"
            expanded
            appearance="ghost"
            aria-label="back-link"
            onClick={backButton}
            loading={isLoading}
          >
            {t('link.back_link')}
          </Button>
        </S.ButtonWrapper>
      )}
      <S.ButtonWrapper>
        <Button
          id="retry-link"
          expanded
          appearance="primary"
          aria-label="retry-link"
          onClick={retryButton}
          loading={isLoading}
        >
          {t('button.try_again')}
        </Button>
      </S.ButtonWrapper>
      {hint && <S.Hint id="loading-hint">{hint}</S.Hint>}
    </S.LoadingButtons>
  )

  const content = (
    <S.LoadingContainer>
      <S.LoadingSpinner>
        <ProgressIndicator />
      </S.LoadingSpinner>
      {message && (
        <S.LoadingMessage>
          <S.Message id="loading-message">{message}</S.Message>
        </S.LoadingMessage>
      )}

      {showButtons && renderButtons()}
    </S.LoadingContainer>
  )

  if (stage === 2 || stage === 3) {
    return (
      <MainLayout>
        <DefaultHeader />
        {content}
      </MainLayout>
    )
  }

  return content
}

export default Loading
